import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useState } from "react";
import { Flower2, Sprout, Users, UsersRound, Sparkles, UserRound, LogOut, Moon, Sun, BarChart3, Gem } from "lucide-react";
import { api } from "../lib/api";

const NAV = [
  { to: "/", label: "My Bouquet", icon: Flower2, testid: "nav-bouquet" },
  { to: "/growing", label: "Growing", icon: Sprout, testid: "nav-growing" },
  { to: "/pods", label: "Pods", icon: Users, testid: "nav-pods" },
  { to: "/community", label: "Community", icon: UsersRound, testid: "nav-community" },
  { to: "/moments", label: "Moments", icon: Sparkles, testid: "nav-moments" },
  { to: "/profile", label: "Profile", icon: UserRound, testid: "nav-profile" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(false);
  const isAdmin = user?.role === "central_admin";

  const toggleDark = () => {
    setDark(!dark);
    document.documentElement.classList.toggle("dark");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col bg-card border-r border-border">
        <div className="px-6 py-5 border-b border-border">
          <img src="/logo-trim.png" alt="Bouquet" data-testid="brand-logo" className="h-14 w-full object-contain rounded-lg" />
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }>
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border space-y-3">
          <NavLink to="/pricing" data-testid="nav-membership"
            className={({ isActive }) =>
              `flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }>
            <Gem className="h-3.5 w-3.5" /> Membership
          </NavLink>
          {isAdmin && (
            <NavLink to="/admin" data-testid="nav-metrics"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }>
              <BarChart3 className="h-3.5 w-3.5" /> Metrics (admin)
            </NavLink>
          )}
          <div className="flex items-center gap-3">
            <Avatar user={user} />
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace("_", " ")}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button data-testid="theme-toggle-button" onClick={toggleDark}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-border text-xs font-medium hover:bg-secondary transition-colors">
              {dark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
              {dark ? "Light" : "Dark"}
            </button>
            <button data-testid="logout-button" onClick={handleLogout}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-border text-xs font-medium hover:bg-destructive hover:text-destructive-foreground transition-colors">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </aside>

      <div className="lg:hidden sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          <img src="/logo-trim.png" alt="Bouquet" className="h-8 w-24 object-contain rounded" />
        </div>
        {isAdmin && (
          <NavLink to="/admin" data-testid="nav-metrics-mobile" className="p-2 rounded-lg text-muted-foreground hover:bg-secondary transition-colors">
            <BarChart3 className="h-4 w-4" />
          </NavLink>
        )}
      </div>

      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border flex" data-testid="mobile-tab-bar">
        {NAV.map(({ to, label, icon: Icon, testid }) => (
          <NavLink key={to} to={to} end={to === "/"} data-testid={`${testid}-mobile`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`
            }>
            <Icon className="h-5 w-5" />
            {label}
          </NavLink>
        ))}
      </nav>

      <main className="lg:pl-64 pb-28 lg:pb-8">
        <div className="px-4 sm:px-8 lg:px-12 py-6 sm:py-8 max-w-6xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export function Avatar({ user, size = "h-9 w-9" }) {
  if (user?.picture) {
    const src = user.picture.startsWith("http") ? user.picture : `${api.defaults.baseURL}/files/${user.picture}`;
    return <img src={src} alt={user.name} className={`${size} rounded-full object-cover shrink-0`} />;
  }
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("");
  return (
    <div className={`${size} rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0`}>
      {initials}
    </div>
  );
}

export function PageHeader({ eyebrow, title, description, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div className="space-y-1">
        {eyebrow && <p className="text-[10px] font-semibold tracking-[0.25em] uppercase text-primary/70">{eyebrow}</p>}
        <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight">{title}</h2>
        {description && <p className="text-sm text-muted-foreground max-w-2xl">{description}</p>}
      </div>
      {children}
    </div>
  );
}

export function Modal({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-background/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose} data-testid={`${testid}-overlay`}>
      <div className="w-full max-w-md bg-card border border-border rounded-xl p-6 sm:p-8 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        {children}
      </div>
    </div>
  );
}
